import React from "react";
import ReactDOM from "react-dom/client";
import { PersonalisationDrawer } from "./components/PersonalisationDrawer";
import { PersonalisationTemplate, XY } from "../types";
import { CustomProductData, getCustomProductData } from "../utils";

/**
  Usage:
    <personalisation-drawer
      tags="{{ product.tags | join: ',' }}"
      handle="{{ product.handle }}"
      variant_id="{{ product.selected_or_first_available_variant.id }}"
    ></personalisation-drawer>
 */
export const PersonalisationDrawerElem = class extends HTMLElement {
  mainImage: string | null = null;
  isOpen = false;
  variantId: string | null = null;
  personalisationPos: XY = { x: null, y: null };
  tags: string[] = []
  triggerElemSelector = "[data-js-open-personalisation-drawer]";
  triggerElems?: NodeListOf<HTMLElement>;
  root = ReactDOM.createRoot(this);
  handle?: string;
  variantImages: CustomProductData["variant_images"] | null = null;
  productVariants = document.querySelector('product-variants');
  template: PersonalisationTemplate | null = null;

  constructor() {
    super();
    this.togglePersonalisation = this.togglePersonalisation.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  async connectedCallback() {
    try {
      this.template = JSON.parse(document.querySelector("[data-personalisation-template]")?.textContent || "null") as PersonalisationTemplate | null;
      this.tags = this.getAttribute("tags")?.split(",") || JSON.parse(document.querySelector("[data-product-tags]")?.textContent || "[]"); 
      this.handle = this.getAttribute("handle") || "";
      this.variantId = this.getAttribute("variant_id");

      const data = await getCustomProductData(this.handle);
      if (!data) throw "No custom data returned";

      this.variantImages = data.variant_images;
      this.personalisationPos = data.personalisation_position;

      this.setMainImage();

      this.triggerElems = document.querySelectorAll(this.triggerElemSelector);
      this.triggerElems?.forEach(elem => elem.addEventListener("click", this.togglePersonalisation));

      // PDP variant Listener
      this.productVariants?.addEventListener('VARIANT_CHANGE', this.handleVariantChange);
    } catch(e) {
      this.onError();
      console.error(e);
    }
  }

  disconnectedCallback() {
    this.triggerElems?.forEach(elem => elem.removeEventListener("click", this.togglePersonalisation));
    this.productVariants?.addEventListener('VARIANT_CHANGE', this.handleVariantChange);
  }

  setMainImage() {
    if (!this.variantImages) return console.error("No variant Image");
    if (!this.variantId) return console.error("No variant Id");

    const variantImage = this.variantImages?.[this.variantId];
    if (variantImage) {
      this.mainImage = `${variantImage}${variantImage.includes("?") ? "&" : "?"}width=340`;
    }
  }

  // PDP only
  handleVariantChange() {
    if (this.productVariants?.currentVariant?.id) {
      this.variantId = this.productVariants.currentVariant.id;
      this.setMainImage();
      this.render();
    }
  }

  togglePersonalisation(e?: any) {
    e?.preventDefault?.();
    this.isOpen = !this.isOpen;
    this.setAttribute('aria-expanded', this.isOpen.toString());
    this.render();
  }

  render() {
    this.root.render(
      <PersonalisationDrawer
        mainImage={this.mainImage}
        closePersonalisation={this.togglePersonalisation}
        personalisationState={this.isOpen}
        selectedVariantId={this.variantId}
        personalisationPos={this.personalisationPos}
        productTags={this.tags}
        template={this.template}
      />
    );
  }

  onError() {
    this.triggerElems?.forEach(elem => elem.classList.add("hidden"));
  }
};

customElements.define("personalisation-drawer", PersonalisationDrawerElem);
